import type { ISODateTimeString } from './BrandedTypes'
import type { CountryRef } from './CountryRef'
import type { CurrencyRef } from './CurrencyRef'

export type StakeholderType = 'other' | 'vendor' | 'client'

export type Stakeholder = OtherParty | Vendor | Client

export type StakeholderBase = {
  /** The ID of the stakeholder. */
  id: string
  /** The name of the stakeholder. */
  name: string
  /** A shorter alternative name for the stakeholder. */
  shortName: string | null
  /** The phone number, in ITU-T E.164 format. */
  phoneNumber: string | null
  /** The email address. */
  email: string | null
  /** The country of the stakeholder. */
  country: CountryRef | null
  /** The postal address in free-form. */
  postalAddress: string | null
  /** The date and time the stakeholder was created. */
  createdAt: ISODateTimeString | null
  /** The date and time the stakeholder was last updated. */
  updatedAt: ISODateTimeString | null
}

export type OtherParty = StakeholderBase & {
  stakeholderType: 'other'
  /** The visit address in free-form. */
  visitAddress: string | null
  /** Whether the visit address is the same as the postal address. */
  visitAddressSameAsPostal: boolean
  /** Whether the other party is inactive. */
  isInactive?: boolean
}

export type Vendor = StakeholderBase & {
  stakeholderType: 'vendor'
  /** The visit address in free-form. */
  visitAddress: string | null
  /** Whether the visit address is the same as the postal address. */
  visitAddressSameAsPostal: boolean
  /** The vendor number (only applicable for vendors). */
  vendorNumber: string | null
  /** The tax identification number (only applicable for clients and vendors) */
  taxIdentificationNumber: string | null
  /** The EORI number (only applicable for clients and vendors). */
  eoriNumber: string | null
  /** Whether the other party is inactive. */
  isInactive?: boolean
}

export type Client = StakeholderBase & {
  stakeholderType: 'client'
  /** The client number (only applicable for clients). */
  clientNumber: string | null
  /** The tax identification number (only applicable for clients and vendors) */
  taxIdentificationNumber: string | null
  /** The EORI number (only applicable for clients and vendors). */
  eoriNumber: string | null
  /** The visit address in free-form. */
  visitAddress: string | null
  /** Whether the visit address is the same as the postal address. */
  visitAddressSameAsPostal: boolean
  /** The billing address in free-form. */
  billingAddress: string | null
  /** Whether the billing address is the same as the postal address. */
  billingAddressSameAsPostal: boolean
  /** The payment period in days. */
  paymentTermsPeriodInDays: number | null
  /** Whether the payment terms are based on the running month. */
  paymentTermsUseRunningMonth: boolean | null
  /** Represents a reference to an active country within the system. */
  billingCurrency: CurrencyRef | null
  /** The invoice email address of the stakeholder. */
  invoiceEmail: string | null
  /** Whether the payment terms defaults to global settings. */
  useDefaultPaymentTerms: Readonly<boolean | null>
  /** Whether the invoice currency defaults to global settings. */
  useDefaultBillingCurrency: Readonly<boolean | null>
  /** Whether the client is inactive. */
  isInactive: boolean
}

export type StakeholderContactType = 'person' | 'other'

export type StakeholderContact =
  | StakeholderContactPerson
  | StakeholderContactOther

export type StakeholderContactBase = {
  /** The ID of the contact. */
  id: string
  /** The ID of the stakeholder. */
  stakeholderId: string
  /** The stakeholder name. */
  stakeholderName: string
  /** A shorter alternative name of the stakeholder. */
  stakeholderShortName: string
  /** The stakeholder type (e.g. "client", "vendor", "other") */
  stakeholderType: StakeholderType
  /** The contact’s work email address. */
  email: string | null
  /** The phone number that dials the contact directly. */
  directLine: string | null
  /** The contact’s mobile phone number. */
  mobilePhone: string | null
  /** Whether the contact is inactive. */
  isInactive: boolean
}

export interface StakeholderContactOther extends StakeholderContactBase {
  /** The type of contact, either "person" or "other". */
  contactType: 'other'
  /** The name when not a person (other type of contact). */
  otherName: string | null
}

export interface StakeholderContactPerson extends StakeholderContactBase {
  /** The type of the contact.*/
  contactType: 'person'
  /** The contact’s last name. */
  lastName: string | null
  /** The contact’s first name. */
  firstName: string | null
  /** The contact’s job title (if person). */
  jobTitle: string | null
  /** The department or business unit where the contact works (if person). */
  department: string | null
}

export function isStakeholderOtherParty(
  stakeholder: Stakeholder
): stakeholder is OtherParty {
  return stakeholder.stakeholderType === 'other'
}

export function isStakeholderVendor(
  stakeholder: Stakeholder
): stakeholder is Vendor {
  return stakeholder.stakeholderType === 'vendor'
}

export function isStakeholderClient(
  stakeholder: Stakeholder
): stakeholder is Client {
  return stakeholder.stakeholderType === 'client'
}

export function isStakeholderContactPerson(
  contact: StakeholderContact
): contact is StakeholderContactPerson {
  return contact.contactType === 'person'
}

export function isStakeholderContactOther(
  contact: StakeholderContact
): contact is StakeholderContactOther {
  return contact.contactType === 'other'
}
